document.addEventListener("DOMContentLoaded", () => {
  const tablists = document.querySelectorAll(".tabs[data-tabgroup]");

  /**
   * Allow only simple slug keys for URL deep-links.
   * Mirrors PHP sanitize_key-ish constraints; never pass raw input elsewhere.
   */
  function normalizeTabKey(value) {
    if (typeof value !== "string") {
      return "";
    }
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .slice(0, 64);
  }

  /**
   * URL param name from data-url-param; only [a-z0-9_-], max 32 chars.
   */
  function normalizeParamName(value) {
    if (typeof value !== "string") {
      return "";
    }
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9_-]/g, "")
      .slice(0, 32);
  }

  tablists.forEach((tablist) => {
    const groupId = tablist.getAttribute("data-tabgroup");
    const tabs = Array.from(tablist.querySelectorAll("[data-tab-target]"));
    if (!groupId || tabs.length === 0) {
      return;
    }

    const panelContainer = document.getElementById(groupId);
    const panels = panelContainer
      ? Array.from(
          panelContainer.querySelectorAll("[role='tabpanel'], .tabwrapper")
        )
      : [];

    const urlParam = normalizeParamName(tablist.getAttribute("data-url-param"));
    const tabsByKey = new Map();

    // Ensure semantics even if markup is incomplete.
    tablist.setAttribute("role", "tablist");

    tabs.forEach((tab, index) => {
      const targetId = tab.getAttribute("data-tab-target");
      if (!tab.id) {
        tab.id = `${groupId}-tab-${index + 1}`;
      }
      tab.setAttribute("role", "tab");
      tab.setAttribute(
        "aria-selected",
        tab.classList.contains("tabs-active") ? "true" : "false"
      );
      tab.setAttribute("aria-controls", targetId);

      const key = normalizeTabKey(
        tab.getAttribute("data-tab-key") || targetId || ""
      );
      if (key) {
        tabsByKey.set(key, tab);
      }

      const panel = panels.find((p) => p.id === targetId);
      if (panel) {
        panel.setAttribute("role", "tabpanel");
        panel.setAttribute("aria-labelledby", tab.id);
        if (!tab.classList.contains("tabs-active")) {
          panel.setAttribute("hidden", "");
        } else {
          panel.removeAttribute("hidden");
        }
      }
    });

    function updateUrlForTab(tab) {
      if (!urlParam || !window.history || !window.history.replaceState) {
        return;
      }

      const key = normalizeTabKey(
        tab.getAttribute("data-tab-key") ||
          tab.getAttribute("data-tab-target") ||
          ""
      );
      if (!key || !tabsByKey.has(key)) {
        return;
      }

      try {
        const nextUrl = new URL(window.location.href);
        nextUrl.searchParams.set(urlParam, key);
        window.history.replaceState({}, "", nextUrl.toString());
      } catch (error) {
        // Ignore malformed location; never throw from tab UI.
      }
    }

    function activateTab(tab, { focus = true, updateUrl = true } = {}) {
      if (!tab) {
        return;
      }
      const targetId = tab.getAttribute("data-tab-target");
      if (!targetId) {
        return;
      }

      tabs.forEach((t) => {
        const isActive = t === tab;
        t.classList.toggle("tabs-active", isActive);
        t.setAttribute("aria-selected", isActive ? "true" : "false");
      });

      panels.forEach((panel) => {
        if (panel.id === targetId) {
          panel.removeAttribute("hidden");
        } else {
          panel.setAttribute("hidden", "");
        }
      });

      if (updateUrl) {
        updateUrlForTab(tab);
      }

      if (focus) {
        tab.focus();
      }
    }

    function focusAdjacentTab(current, direction) {
      const index = tabs.indexOf(current);
      if (index === -1) {
        return;
      }
      let nextIndex = index + direction;
      if (nextIndex < 0) {
        nextIndex = tabs.length - 1;
      } else if (nextIndex >= tabs.length) {
        nextIndex = 0;
      }
      const nextTab = tabs[nextIndex];
      nextTab.focus();
    }

    tabs.forEach((tab) => {
      tab.addEventListener("click", (event) => {
        event.preventDefault();
        activateTab(tab, { focus: false, updateUrl: true });
      });

      tab.addEventListener("keydown", (event) => {
        switch (event.key) {
          case "ArrowRight":
          case "ArrowDown":
            event.preventDefault();
            focusAdjacentTab(tab, 1);
            break;
          case "ArrowLeft":
          case "ArrowUp":
            event.preventDefault();
            focusAdjacentTab(tab, -1);
            break;
          case "Home":
            event.preventDefault();
            tabs[0].focus();
            break;
          case "End":
            event.preventDefault();
            tabs[tabs.length - 1].focus();
            break;
          case " ":
          case "Enter":
            event.preventDefault();
            activateTab(tab, { focus: false, updateUrl: true });
            break;
          default:
            break;
        }
      });
    });

    // Deep-link: only accept keys that exist on this tablist (allowlist).
    let tabFromUrl = null;
    if (urlParam) {
      try {
        const raw = new URLSearchParams(window.location.search).get(urlParam);
        const key = normalizeTabKey(raw || "");
        if (key) {
          tabFromUrl = tabsByKey.get(key) || null;
        }
      } catch (error) {
        tabFromUrl = null;
      }
    }

    const activeTab =
      tabFromUrl ||
      tabs.find((tab) => tab.classList.contains("tabs-active")) ||
      tabs[0];

    // Do not rewrite the URL on initial paint when PHP already set the right tab
    // (or when there is no url-param binding).
    activateTab(activeTab, { focus: false, updateUrl: false });

    // Optional: scroll to the section when a deep-link param was present.
    if (tabFromUrl && urlParam) {
      const section = tablist.closest("section[id]");
      if (section && typeof section.scrollIntoView === "function") {
        window.requestAnimationFrame(() => {
          section.scrollIntoView({ block: "start", behavior: "smooth" });
        });
      }
    }
  });
});
